import type { CivilTimeZones } from '../civil-date';

/**
 * Instant d'un champ `datetime` (R1, ADR-085) : l'opérateur lit et tape l'heure murale du site ; la
 * réponse part en instant ISO 8601 UTC. Le fuseau est celui du site, sinon du tenant (contexte de
 * résolution `civil_timezone`, ADR-141 addendum du 2026-10-18), jamais l'UTC ni le fuseau du poste par
 * défaut : sans fuseau connu, aucune conversion.
 */

const DAY_MS = 86_400_000;
const LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/;

/** Premier fuseau IANA valide : le site, puis le tenant ; `null` sinon. */
export function civilTimeZone(zones: CivilTimeZones): string | null {
  for (const zone of [zones.site, zones.tenant]) {
    if (typeof zone !== 'string' || zone === '') continue;
    try {
      new Intl.DateTimeFormat('en-CA', { timeZone: zone });
      return zone;
    } catch {
      // Fuseau inconnu : le suivant.
    }
  }
  return null;
}

interface WallClock {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
}

function wallClockOf(instant: number, zone: string): WallClock {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-CA', { timeZone: zone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
      .formatToParts(new Date(instant))
      .map((part) => [part.type, part.value]),
  );
  return { year: Number(parts['year']), month: Number(parts['month']), day: Number(parts['day']), hour: Number(parts['hour']), minute: Number(parts['minute']) };
}

function utcOf(clock: WallClock): number {
  return Date.UTC(clock.year, clock.month - 1, clock.day, clock.hour, clock.minute);
}

/** Décalage du fuseau à cet instant, en millisecondes (heure murale − UTC), à la minute. */
function offsetAt(instant: number, zone: string): number {
  const minute = Math.floor(instant / 60_000) * 60_000;
  return utcOf(wallClockOf(minute, zone)) - minute;
}

const pad = (value: number, size = 2): string => String(value).padStart(size, '0');

/** Heure murale « AAAA-MM-JJTHH:MM » d'un instant dans le fuseau ; `''` pour une valeur illisible. */
export function wallClockInput(value: unknown, zone: string): string {
  if (typeof value !== 'string' || value === '') return '';
  const instant = Date.parse(value);
  if (Number.isNaN(instant)) return '';
  const clock = wallClockOf(instant, zone);
  return `${pad(clock.year, 4)}-${pad(clock.month)}-${pad(clock.day)}T${pad(clock.hour)}:${pad(clock.minute)}`;
}

/**
 * Instant ISO 8601 UTC d'une heure murale du fuseau. `null` pour une saisie illisible ou une heure qui
 * n'existe pas ce jour-là (passage à l'heure d'été) ; une heure vécue deux fois (retour à l'heure
 * d'hiver) prend la première.
 */
export function instantFromWallClock(local: string, zone: string): string | null {
  const match = LOCAL.exec(local);
  if (match === null) return null;
  const clock: WallClock = { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]), hour: Number(match[4]), minute: Number(match[5]) };
  const naive = utcOf(clock);
  // Décalages possibles autour de cette heure (la veille, le lendemain, sur place) : une heure qui
  // existe en a un qui la retrouve ; vécue deux fois, la première occurrence (instant le plus tôt).
  const offsets = new Set([offsetAt(naive - DAY_MS, zone), offsetAt(naive, zone), offsetAt(naive + DAY_MS, zone)]);
  const matches = [...offsets].map((offset) => naive - offset).filter((candidate) => utcOf(wallClockOf(candidate, zone)) === naive);
  if (matches.length === 0) return null;
  const instant = Math.min(...matches);
  return new Date(instant).toISOString();
}

/** Nom lisible du fuseau (« heure d’Europe centrale ») ; à défaut, le nom de la ville. */
export function zoneLabel(zone: string, at: Date = new Date()): string {
  try {
    const name = new Intl.DateTimeFormat('fr-FR', { timeZone: zone, timeZoneName: 'longGeneric' }).formatToParts(at).find((part) => part.type === 'timeZoneName')?.value;
    if (name !== undefined && name !== '') return name;
  } catch {
    // Pas de nom générique : la ville.
  }
  const city = zone.split('/').pop() ?? zone;
  return `heure de ${city.replace(/_/g, ' ')}`;
}
